import { zodResolver } from '@hookform/resolvers/zod';
import { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AcceptInvitationHeader } from '@/features/partners/components/layouts/accept-partner-invitation-header';
import { useAcceptSubprojectInvitationStore } from '@/features/partners/store/use-accept-subproject-invitation-store';
import { useCategoriesQuery } from '@/features/projects/categories/hooks/use-categories-query';
import {
  FlowBackButton,
  FlowSubmitButton,
} from '@/shared/components/flow/flow-actions';
import { FlowFormFields } from '@/shared/components/flow/flow-fields';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';

const formSchema = (
  t: TFunction<'subproject', 'AcceptInvitation.CategoryForm'>,
) =>
  z.object({
    categoryId: z.string().min(1, t('form_field.category.required')),
  });

export const AcceptInvitationCategoryPage = () => {
  const { t } = useTranslation('subproject', {
    keyPrefix: 'AcceptInvitation.CategoryForm',
  });
  const { t: categoryT } = useTranslation('categories', {
    keyPrefix: 'Level2',
  });

  const { data: acceptData, setData: setAcceptData } =
    useAcceptSubprojectInvitationStore();
  const { data: categoriesQueryData } = useCategoriesQuery();
  const categories = categoriesQueryData?.categories;

  const form = useForm({
    resolver: zodResolver(formSchema(t)),
    defaultValues: {
      categoryId: acceptData.selectedCategory?.id || '',
    },
  });

  function onSubmit() {
    const values = form.getValues();

    const selectedCategory = categories?.level2.find(
      (cat) => cat.id === values.categoryId,
    );

    setAcceptData({
      selectedCategory,
      step: 'subcategory',
    });
  }

  return (
    <>
      <AcceptInvitationHeader />
      <Card variant="form" wrapWithStars>
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent>
              <FlowFormFields>
                <FormField
                  name="categoryId"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger isInputTrigger>
                            <SelectValue
                              placeholder={t('category_placeholder')}
                              asLabelInput
                              label={t('category_label')}
                            >
                              {field.value
                                ? categoryT(field.value as any)
                                : null}
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories?.level2.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {categoryT(category.id as any)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FlowFormFields>
            </CardContent>
            <CardFooter>
              <FlowBackButton
                onClick={() => {
                  setAcceptData({
                    step: 'info',
                  });
                }}
              >
                {t('back_button_label')}
              </FlowBackButton>
              <FlowSubmitButton>{t('submit_button_label')}</FlowSubmitButton>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </>
  );
};
